/**
 * Brand Colors
 *
 * Consistent color assignments for each clinic/brand across the application.
 * Used in charts, badges, and other visual elements.
 *
 * Mirrors backend/src/config/brandColors.js, which carries the note on why
 * this table exists once rather than six times. brandColors.test.js fails if
 * the two drift apart.
 */

export const BRAND_COLORS = {
  tmates: '#3B82F6',            // Blue
  mdexam: '#22C55E',            // Green
  mdexam_legacy: '#A855F7',     // Purple (temporary legacy support)
  rose: '#F43F5E',              // Rose
  'fc2-bluestone': '#6366F1',   // Indigo
  bloom_greenstone: '#14B8A6',  // Teal (marketplace, custom P&L)
  mdrx: '#F97316',              // Orange
  vigore: '#0EA5E9',            // Sky
  revora: '#D946EF',            // Fuchsia
  evamd: '#84CC16',             // Lime
  trybalance: '#EAB308',        // Yellow
  scienceco: '#06B6D4',         // Cyan
  rxbloom: '#EC4899',           // Pink
  'mdexam-wl': '#78716C',       // Stone
  'mdexam-rrv': '#A16207',      // Bronze
  getfitmd: '#64748B',          // Slate (local development clinic)
}

// Fallback color for unknown brands
export const DEFAULT_BRAND_COLOR = '#6B7280' // Gray

/**
 * Names that appear in ad-spend data but are not clinic ids.
 */
export const BRAND_ALIASES = {
  bluestone: 'fc2-bluestone',
  mdexam_wl: 'mdexam-wl',
  mdexam_rrv: 'mdexam-rrv',
  greenstone: 'bloom_greenstone',
  bloom: 'bloom_greenstone',
  science: 'scienceco',
  'science.co': 'scienceco',
}

/**
 * Get color for a clinic/brand ID
 * @param {string} clinicId - The clinic identifier or ad-spend brand name
 * @returns {string} Hex color code
 */
export function getBrandColor(clinicId) {
  if (!clinicId) return DEFAULT_BRAND_COLOR
  const key = String(clinicId).toLowerCase().trim()
  return BRAND_COLORS[key] || BRAND_COLORS[BRAND_ALIASES[key]] || DEFAULT_BRAND_COLOR
}

/**
 * Get all brand colors as an array (for chart legends)
 * @returns {Array<{id: string, color: string}>}
 */
export function getAllBrandColors() {
  return Object.entries(BRAND_COLORS).map(([id, color]) => ({ id, color }))
}
